/**
 * QuestionsStep
 *
 * Conditional questions based on user segment - redesigned with
 * world-class editorial aesthetics. Inspired by Collins, Pentagram.
 *
 * Design Philosophy:
 * - Elegant card interactions with subtle depth
 * - Thoughtful motion choreography
 * - Clear visual hierarchy
 * - Quiet luxury feel
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Clock,
  DollarSign,
  Target,
  Sparkles,
  ChevronRight,
  Sofa,
  BedDouble,
  ChefHat,
  UtensilsCrossed,
  Monitor,
  LayoutGrid,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/cn';
import type { MonthlyPropertyVolume, PrimaryPainPoint } from '../../../lib/supabase';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
  OnboardingButton,
} from '../OnboardingLayout';
import type { OnboardingStepProps } from '../OnboardingRouter';

// Pro user question options
const VOLUME_OPTIONS: { value: MonthlyPropertyVolume; label: string; description: string }[] = [
  { value: '1-3', label: '1-3 properties', description: 'Just getting started' },
  { value: '4-10', label: '4-10 properties', description: 'Active and growing' },
  { value: '11-25', label: '11-25 properties', description: 'High volume' },
  { value: '26+', label: '26+ properties', description: 'Top producer' },
];

const PAIN_POINT_OPTIONS: {
  value: PrimaryPainPoint;
  label: string;
  description: string;
  icon: typeof TrendingUp;
}[] = [
  {
    value: 'visualization',
    label: 'Helping buyers visualize',
    description: 'Empty rooms are hard to imagine',
    icon: Sparkles,
  },
  {
    value: 'cost',
    label: 'Staging costs too much',
    description: 'Physical staging eats into margins',
    icon: DollarSign,
  },
  {
    value: 'speed',
    label: 'Time to market',
    description: 'Listings need to go live fast',
    icon: Clock,
  },
  {
    value: 'competition',
    label: 'Standing out',
    description: 'Compete with better photos',
    icon: Target,
  },
  {
    value: 'differentiation',
    label: 'Elevating my service',
    description: 'Offer more to clients',
    icon: TrendingUp,
  },
];

// Consumer room type options with icons instead of emojis
const ROOM_OPTIONS: { value: string; label: string; icon: typeof Sofa }[] = [
  { value: 'living_room', label: 'Living Room', icon: Sofa },
  { value: 'bedroom', label: 'Bedroom', icon: BedDouble },
  { value: 'kitchen', label: 'Kitchen', icon: ChefHat },
  { value: 'dining_room', label: 'Dining Room', icon: UtensilsCrossed },
  { value: 'home_office', label: 'Home Office', icon: Monitor },
  { value: 'other', label: 'Other', icon: LayoutGrid },
];

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function QuestionsStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const { profile } = useAuth();
  const segment = profile?.user_segment;

  // Determine which path to show
  const isPro = segment === 'agent' || segment === 'photographer' || segment === 'brokerage';
  const isConsumer = segment === 'fsbo' || segment === 'homeowner';

  // Skip questions for 'other' segment
  if (segment === 'other') {
    // Auto-advance to staging
    onStepComplete('questions', { skipped_pro_questions: true });
    return null;
  }

  if (isPro) {
    return <ProQuestions onStepComplete={onStepComplete} onboardingData={onboardingData} />;
  }

  if (isConsumer) {
    return <ConsumerQuestions onStepComplete={onStepComplete} onboardingData={onboardingData} />;
  }

  // Fallback: skip to staging
  onStepComplete('questions');
  return null;
}

/**
 * Pro Questions Component
 * Two-step flow: Volume -> Pain Point
 */
function ProQuestions({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const [step, setStep] = useState<'volume' | 'pain_point'>('volume');
  const [volume, setVolume] = useState<MonthlyPropertyVolume | null>(null);
  const [painPoint, setPainPoint] = useState<PrimaryPainPoint | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVolumeSelect = (value: MonthlyPropertyVolume) => {
    setVolume(value);
    // Auto-advance to pain point after brief delay
    setTimeout(() => setStep('pain_point'), 350);
  };

  const handlePainPointSelect = async (value: PrimaryPainPoint) => {
    setPainPoint(value);
    setIsSubmitting(true);

    try {
      // Track analytics
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_questions_completed',
          path: 'pro',
          volume,
          pain_point: value,
          session_id: onboardingData.session_id,
        });
      }

      await onStepComplete('questions', {
        monthly_property_volume: volume!,
        primary_pain_point: value,
      });
    } catch (error) {
      console.error('Error saving pro questions:', error);
      await onStepComplete('questions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await onStepComplete('questions', {
        monthly_property_volume: volume || undefined,
        skipped_pro_questions: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      showProgress={true}
      showBack={step === 'pain_point'}
      onBack={() => setStep('volume')}
    >
      <OnboardingStepContainer maxWidth="2xl">
        <AnimatePresence mode="wait">
          {step === 'volume' ? (
            <motion.div
              key="volume"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <OnboardingHeading
                eyebrow="Tell us about you"
                title="How many properties do you work with monthly?"
                subtitle="This helps us recommend the right plan for your workflow."
              />

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
              >
                {VOLUME_OPTIONS.map((option) => {
                  const isSelected = volume === option.value;
                  return (
                    <motion.button
                      key={option.value}
                      variants={itemVariants}
                      onClick={() => handleVolumeSelect(option.value)}
                      disabled={isSubmitting}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className={cn(
                        'relative p-6 rounded-2xl text-left transition-all duration-300',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                        isSelected
                          ? 'bg-charcoal text-alabaster shadow-lift'
                          : 'bg-white border border-stone/30 hover:border-stone/50 hover:shadow-soft'
                      )}
                    >
                      <h3
                        className={cn(
                          'font-sans font-semibold text-lg mb-1 transition-colors',
                          isSelected ? 'text-alabaster' : 'text-charcoal'
                        )}
                      >
                        {option.label}
                      </h3>
                      <p
                        className={cn(
                          'text-sm font-sans transition-colors',
                          isSelected ? 'text-alabaster/70' : 'text-sand'
                        )}
                      >
                        {option.description}
                      </p>

                      {/* Selection indicator */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                          className="absolute top-4 right-4 size-6 bg-olive rounded-full flex items-center justify-center"
                        >
                          <svg
                            className="size-3.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </motion.div>
                      )}

                      {/* Arrow hint */}
                      <div
                        className={cn(
                          'absolute bottom-4 right-4 size-8 rounded-full flex items-center justify-center transition-all',
                          isSelected
                            ? 'bg-white/20'
                            : 'bg-transparent group-hover:bg-stone/20'
                        )}
                      >
                        <ChevronRight
                          className={cn(
                            'size-4 transition-colors',
                            isSelected ? 'text-alabaster' : 'text-sand'
                          )}
                        />
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <button
                  onClick={handleSkip}
                  className="text-sand hover:text-charcoal text-sm font-sans transition-colors duration-200"
                >
                  Skip for now
                </button>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="pain_point"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <OnboardingHeading
                eyebrow="One more thing"
                title="What's your biggest challenge?"
                subtitle="We'll show you how VirtualStagePro solves it."
              />

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-3 mb-10"
              >
                {PAIN_POINT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = painPoint === option.value;

                  return (
                    <motion.button
                      key={option.value}
                      variants={itemVariants}
                      onClick={() => handlePainPointSelect(option.value)}
                      disabled={isSubmitting}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.99 }}
                      className={cn(
                        'group relative w-full p-5 rounded-2xl text-left transition-all duration-300',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                        isSelected
                          ? 'bg-charcoal shadow-lift'
                          : 'bg-white border border-stone/30 hover:border-stone/50 hover:shadow-soft'
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={cn(
                            'flex-shrink-0 size-12 rounded-xl flex items-center justify-center transition-colors duration-300',
                            isSelected ? 'bg-olive' : 'bg-stone/20 group-hover:bg-olive/10'
                          )}
                        >
                          <Icon
                            className={cn(
                              'size-5 transition-colors duration-300',
                              isSelected ? 'text-white' : 'text-sand group-hover:text-olive'
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3
                            className={cn(
                              'font-sans font-semibold transition-colors',
                              isSelected ? 'text-alabaster' : 'text-charcoal'
                            )}
                          >
                            {option.label}
                          </h3>
                          <p
                            className={cn(
                              'text-sm font-sans transition-colors',
                              isSelected ? 'text-alabaster/70' : 'text-sand'
                            )}
                          >
                            {option.description}
                          </p>
                        </div>

                        {/* Arrow */}
                        <ChevronRight
                          className={cn(
                            'size-5 flex-shrink-0 transition-all duration-300',
                            isSelected
                              ? 'text-alabaster translate-x-0'
                              : 'text-stone -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
                          )}
                        />
                      </div>

                      {/* Selection indicator */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                          className="absolute top-3 right-3 size-5 bg-olive rounded-full flex items-center justify-center"
                        >
                          <svg
                            className="size-3 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center"
              >
                <button
                  onClick={handleSkip}
                  className="text-sand hover:text-charcoal text-sm font-sans transition-colors duration-200"
                >
                  Skip for now
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading overlay */}
        {isSubmitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-alabaster/90 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <div className="text-center">
              <div className="size-12 border-2 border-charcoal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sand font-sans text-sm">Personalizing your experience</p>
            </div>
          </motion.div>
        )}
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}

/**
 * Consumer Questions Component
 * Simple room type selection with elegant grid
 */
function ConsumerQuestions({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRoomSelect = async (value: string) => {
    setSelectedRoom(value);
    setIsSubmitting(true);

    try {
      // Track analytics
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_questions_completed',
          path: 'consumer',
          room_type: value,
          session_id: onboardingData.session_id,
        });
      }

      await onStepComplete('questions', {
        first_room_type_selected: value,
      });
    } catch (error) {
      console.error('Error saving consumer questions:', error);
      await onStepComplete('questions');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingLayout showProgress={true}>
      <OnboardingStepContainer maxWidth="2xl">
        <OnboardingHeading
          eyebrow="Let's get started"
          title="Which room would you like to stage first?"
          subtitle="Pick one to begin - you can stage more rooms later."
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10"
        >
          {ROOM_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedRoom === option.value;

            return (
              <motion.button
                key={option.value}
                variants={itemVariants}
                onClick={() => handleRoomSelect(option.value)}
                disabled={isSubmitting}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  'group relative p-6 sm:p-8 rounded-2xl text-center transition-all duration-300',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                  isSelected
                    ? 'bg-charcoal shadow-lift'
                    : 'bg-white border border-stone/30 hover:border-stone/50 hover:shadow-soft'
                )}
              >
                {/* Icon */}
                <div
                  className={cn(
                    'size-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors duration-300',
                    isSelected ? 'bg-olive' : 'bg-stone/20 group-hover:bg-olive/10'
                  )}
                >
                  <Icon
                    className={cn(
                      'size-7 transition-colors duration-300',
                      isSelected ? 'text-white' : 'text-sand group-hover:text-olive'
                    )}
                  />
                </div>

                <h3
                  className={cn(
                    'font-sans font-semibold transition-colors',
                    isSelected ? 'text-alabaster' : 'text-charcoal'
                  )}
                >
                  {option.label}
                </h3>

                {/* Selection indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                    className="absolute top-3 right-3 size-6 bg-olive rounded-full flex items-center justify-center"
                  >
                    <svg
                      className="size-3.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Loading overlay */}
        {isSubmitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-alabaster/90 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <div className="text-center">
              <div className="size-12 border-2 border-charcoal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sand font-sans text-sm">Getting your studio ready</p>
            </div>
          </motion.div>
        )}
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
